import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, Heart, Baby, Droplet, Activity, CheckCircle, Building, Lightbulb, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

export default function Projects() {
  const ongoingProjects = [
    {
      icon: Heart,
      title: 'Community Health Outreach Program',
      status: 'Ongoing',
      type: 'Health Outreach',
      description: 'Regular health screening camps and health education sessions in villages across Bagamoyo and surrounding areas. This initiative aims to bring healthcare closer to communities with limited access to medical facilities.',
      impact: ['5,000+ patients screened annually', 'Health education for 20+ villages', 'Free medication distribution', 'Referral services for complex cases'],
      image: 'https://images.unsplash.com/photo-1579165466741-7f35e4755660?w=600'
    },
    {
      icon: Baby,
      title: 'Safe Motherhood Initiative',
      status: 'Ongoing',
      type: 'Maternal Health',
      description: 'Comprehensive maternal health program providing prenatal care, skilled delivery services, and postnatal support to expectant mothers in the Mapinga community and surrounding areas.',
      impact: ['1,000+ safe deliveries per year', 'Reduced maternal mortality', 'Trained community midwives', 'Emergency obstetric care'],
      image: 'https://images.unsplash.com/photo-1766299893214-fec03a6ed973?w=600'
    },
    {
      icon: Activity,
      title: 'Non-Communicable Disease Prevention',
      status: 'Ongoing',
      type: 'Disease Prevention',
      description: 'Awareness and screening program for diabetes, hypertension, and cardiovascular diseases in the community, with follow-up care and lifestyle counseling.',
      impact: ['Early detection of chronic conditions', 'Lifestyle counseling programs', 'Medication access support', 'Regular monitoring services'],
      image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=600'
    }
  ];

  const upcomingProjects = [
    {
      icon: Building,
      title: 'Hospital Wing Expansion',
      type: 'Infrastructure Development',
      description: 'Planned expansion of hospital facilities to increase bed capacity and add new specialized departments to better serve the growing community needs.',
      timeline: 'Planned'
    },
    {
      icon: Lightbulb,
      title: 'Telemedicine Service',
      type: 'Service Improvement',
      description: 'Implementation of telemedicine services to extend healthcare access to remote areas and provide specialist consultations virtually.',
      timeline: 'In Planning'
    },
    {
      icon: Wrench,
      title: 'Medical Equipment Upgrade',
      type: 'Equipment',
      description: 'Acquisition of advanced medical diagnostic equipment to enhance our diagnostic capabilities and treatment options.',
      timeline: 'In Planning'
    }
  ];

  const completedProjects = [
    {
      icon: Droplet,
      title: 'Blood Donation Campaign',
      type: 'Community Health',
      description: 'Established regular blood donation drives in partnership with health authorities to ensure adequate blood supply.',
      outcome: '500+ units collected annually'
    },
    {
      icon: Users,
      title: 'Healthcare Worker Training',
      type: 'Capacity Building',
      description: 'Training program for community health workers to extend healthcare services to remote areas.',
      outcome: '100+ health workers trained'
    },
    {
      icon: Target,
      title: 'School Health Program',
      type: 'Child Health',
      description: 'Health screenings, deworming, and health education programs implemented in schools across the district.',
      outcome: '15+ schools, 3,000+ children reached'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary py-20 md:py-28" data-testid="projects-hero">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-accent font-semibold mb-3 tracking-wide uppercase text-sm">About Us</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Our Projects
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Beyond hospital walls, Baobab Hospital is committed to improving community health through various outreach programs, health initiatives, infrastructure development, and service improvement programs.
            </p>
          </div>
        </div>
      </section>

      {/* Ongoing Projects */}
      <section className="section-spacing" data-testid="projects-ongoing">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Active Initiatives</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Ongoing Projects
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These are our current health outreach and community development initiatives making a difference in the lives of our community members.
            </p>
          </div>

          <div className="space-y-12">
            {ongoingProjects.map((project, index) => (
              <Card key={index} className="overflow-hidden" data-testid={`ongoing-project-${index}`}>
                <div className="grid lg:grid-cols-2">
                  <div className="aspect-video lg:aspect-auto overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-8 lg:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center">
                        <project.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-accent uppercase tracking-wider">{project.type}</span>
                        <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">{project.status}</span>
                      </div>
                    </div>
                    <h3 className="font-heading font-bold text-primary text-2xl mb-4">{project.title}</h3>
                    <p className="text-slate-600 mb-6">{project.description}</p>
                    <div className="space-y-2">
                      <p className="font-semibold text-primary text-sm">Key Impact:</p>
                      {project.impact.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Projects */}
      <section className="section-spacing bg-warm" data-testid="projects-upcoming">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Future Initiatives</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Upcoming Projects
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Projects in development to further enhance our healthcare services and infrastructure.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {upcomingProjects.map((project, index) => (
              <Card key={index} className="card-service" data-testid={`upcoming-project-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                      <project.icon className="w-6 h-6 text-accent" />
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">{project.timeline}</span>
                  </div>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{project.type}</span>
                  <h3 className="font-heading font-semibold text-primary text-lg mt-2 mb-3">{project.title}</h3>
                  <p className="text-slate-600 text-sm">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Completed Projects */}
      <section className="section-spacing" data-testid="projects-completed">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Achievements</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Completed Projects
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Successfully implemented initiatives that have made a lasting impact on our community.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {completedProjects.map((project, index) => (
              <Card key={index} className="bg-teal-light" data-testid={`completed-project-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4">
                    <project.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">{project.type}</span>
                  <h3 className="font-heading font-semibold text-primary text-lg mt-2 mb-3">{project.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{project.description}</p>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-sm font-semibold text-accent">{project.outcome}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Summary */}
      <section className="py-20 bg-primary" data-testid="projects-impact">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Making a Difference</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Our Impact</h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Through our community projects, we have made a significant impact on public health in the Pwani region.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-bold text-accent">15,000+</p>
              <p className="text-white/80 mt-2">Community Members Reached</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-bold text-accent">6+</p>
              <p className="text-white/80 mt-2">Active Programs</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-bold text-accent">20+</p>
              <p className="text-white/80 mt-2">Villages Covered</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-bold text-accent">150+</p>
              <p className="text-white/80 mt-2">Health Workers Trained</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-accent" data-testid="projects-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Support Our Community Projects
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Your support can help us expand our reach and impact more lives in the Mapinga community and beyond.
          </p>
          <Link to="/contact" data-testid="projects-contact">
            <Button className="bg-white text-accent hover:bg-slate-100 rounded-full px-8 py-4 font-semibold shadow-lg">
              Get Involved
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
